import { useState } from "react"

import { formatUsd } from "../BatteryOnlyProposal/view-helpers"
import { SectionRail, SolarFooter, SolarHeader } from "./sections/chrome"
import { Financing } from "./sections/financing"
import { Hero } from "./sections/hero"
import { Savings } from "./sections/savings"
import {
  ClosingCta,
  Environment,
  PowurCare,
  SolarHomeownerApp,
  SolarNextSteps,
} from "./sections/story"
import { Components, Home, SystemSpecs } from "./sections/system"
import type { SolarPresentationProposal } from "./solar-presentation"
import { SOLAR_SECTION_LINKS } from "./solar-section-links"

/** The sample keeps its own Financing section, has no pricing read and no AI compute node. */
const SAMPLE_SECTION_LINKS = SOLAR_SECTION_LINKS.filter(
  (link) => link.id !== `investment` && link.id !== `ai-compute`,
)

/**
 * The design reference's Solar presentation in section order, rendered from
 * the sample fixture at `/solar-proposal/sample`. The selected finance option is the one piece of page state: the
 * Financing section picks it, and the closing CTA describes it. The hero
 * states "The math" figure at the default bill, as a real proposal's hero
 * states its savings section's, so it doesn't move with the finance option.
 */
export function SolarPresentation({
  proposal,
}: {
  proposal: SolarPresentationProposal
}): JSX.Element | null {
  const [selectedId, setSelectedId] = useState(proposal.defaultFinanceOptionId)
  const selected =
    proposal.financeOptions.find((option) => option.id === selectedId) ??
    proposal.financeOptions[0]
  if (!selected) {
    return null
  }
  const { savings } = proposal
  const defaultScenario = savings.scenarios.find(
    (scenario) => scenario.monthlyBill === savings.defaultMonthlyBill,
  )
  const ctaPayment =
    selected.paymentCadence === `monthly`
      ? `${formatUsd(selected.payment)}/mo`
      : formatUsd(selected.payment)

  return (
    <>
      <SolarHeader headline={proposal} />
      <SectionRail sections={SAMPLE_SECTION_LINKS} />
      <Hero
        headline={proposal}
        savings={
          defaultScenario
            ? {
                familyName: proposal.familyName,
                lifetimeSavings: defaultScenario.totalSavings,
                termYears: savings.termYears,
                kind: `utility`,
                breakEvenYear: null,
              }
            : null
        }
      />
      <Home residence={proposal.residence} />
      <SystemSpecs system={proposal.system} />
      <Components components={proposal.components} />
      <Savings savings={savings} />
      <Financing
        proposal={proposal}
        selected={selected}
        onSelect={setSelectedId}
      />
      <PowurCare />
      <SolarNextSteps />
      <Environment impact={proposal.impact} />
      <SolarHomeownerApp />
      <ClosingCta proposal={proposal} monthlyPayment={ctaPayment} />
      <SolarFooter proposal={proposal} />
    </>
  )
}
